import type { ReactNode } from 'react';

import { useWizard } from 'react-use-wizard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@client/components/ui/button';

interface GuidedPlanningFooterProps {
  isNextDisabled?: boolean;
  nextButtonLabel?: string;
  backButtonLabel?: string;
  lastStepButtonLabel?: string;
  nextButtonIcon?: ReactNode;
  backButtonIcon?: ReactNode;
  nextButtonVariant?: 'default' | 'outline' | 'ghost';
  onComplete?: () => void;
  onNext?: () => void;
  onBack?: () => void;
  hideBack?: boolean;
  isLoading?: boolean;
}

export function GuidedPlanningFooter({
  isNextDisabled,
  nextButtonLabel,
  backButtonLabel,
  lastStepButtonLabel = 'Finish',
  nextButtonIcon,
  backButtonIcon,
  nextButtonVariant = 'default',
  onComplete,
  onNext,
  onBack,
  hideBack,
  isLoading: isLoadingProp,
}: GuidedPlanningFooterProps) {
  const {
    nextStep,
    previousStep,
    isFirstStep,
    isLastStep,
    isLoading: isWizardLoading,
  } = useWizard();

  const isLoading = isLoadingProp || isWizardLoading;
  const showBackButton = !hideBack && (onBack || !isFirstStep);

  const handleNext = () => {
    if (onNext) return onNext();
    if (isLastStep && onComplete) return onComplete();
    nextStep();
  };

  const handleBack = onBack ?? previousStep;

  const buttonLabel = nextButtonLabel ?? (isLastStep && !onNext ? lastStepButtonLabel : 'Next');
  const backLabel = backButtonLabel ?? 'Back';
  const BackIcon = backButtonIcon ?? <ChevronLeft className="size-5" />;
  const NextIcon = nextButtonIcon ?? <ChevronRight className="size-5" />;

  return (
    <div className="flex flex-col-reverse items-center gap-3 py-6 sm:flex-row sm:justify-center sm:gap-4 sm:py-8">
      {showBackButton && (
        <Button
          size="lg"
          variant="outline"
          onClick={handleBack}
          disabled={isLoading}
          className="w-full rounded-full px-6 py-3 text-base sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
        >
          {BackIcon}
          {backLabel}
        </Button>
      )}
      <Button
        size="lg"
        variant={nextButtonVariant}
        onClick={handleNext}
        disabled={isNextDisabled || isLoading}
        className="w-full rounded-full px-6 py-3 text-base sm:w-auto sm:px-8 sm:py-4 sm:text-lg"
      >
        {buttonLabel}
        {NextIcon}
      </Button>
    </div>
  );
}
